/* eslint-disable default-case */
/* eslint-disable consistent-return */
import {ofType} from 'redux-observable';
import {tap, ignoreElements, filter, mergeMap} from 'rxjs/operators';
import {of} from 'rxjs';

import {combineEpics} from 'web-app/util/redux-observable';
import {
    FILE_UPLOAD_FILE_UPLOAD_SUCCESS,
    FILE_UPLOAD_FILE_UPLOAD_FAILED,
    FILE_UPLOAD_ADD_FILE,
} from 'web-app/react/constants/action-types';
import {track} from 'web-app/util/famlytics';
import {TrackingEvents} from 'web-app/util/famlytics/events';

import {captureVideoMetaData, type CompressionConfig, isCompressibleFile} from './typed-helpers';
import * as Actions from './actions';

/*
    Tracking file uploads in the simplest way possible.

    Sidenote: this could be implemented with rx magic, but I chose this way as it'll be much easier to maintain
*/
enum UploadAction {
    FILE_UPLOAD_FILE_UPLOAD_SUCCESS = 'FILE_UPLOAD_FILE_UPLOAD_SUCCESS',
    FILE_UPLOAD_FILE_UPLOAD_FAILED = 'FILE_UPLOAD_FILE_UPLOAD_FAILED',
}

const fileActionToEventType = (actionType: UploadAction) => {
    switch (actionType) {
        case UploadAction.FILE_UPLOAD_FILE_UPLOAD_SUCCESS:
            return TrackingEvents.FILE_UPLOAD_SUCCEEDED;
        case UploadAction.FILE_UPLOAD_FILE_UPLOAD_FAILED:
            return TrackingEvents.FILE_UPLOAD_FAILED;
    }
};

type TimestampInMs = number;
const fileUploadTimeMap: {
    [fileId: string]: TimestampInMs;
} = {};

const listenForFileUpload = action$ =>
    action$.pipe(
        ofType(FILE_UPLOAD_ADD_FILE),
        tap(({payload}) => {
            if (payload && payload.file) {
                fileUploadTimeMap[payload.file.id] = Date.now();
            }
        }),
        ignoreElements(),
    );

const listenForFileUploadCompletion = action$ =>
    action$.pipe(
        ofType(FILE_UPLOAD_FILE_UPLOAD_SUCCESS, FILE_UPLOAD_FILE_UPLOAD_FAILED),
        tap(({type, payload}) => {
            if (isCompressibleFile(payload.uncompressedFile)) {
                track(fileActionToEventType(type as UploadAction), {
                    optimizationsEnabled: Boolean((payload.compressionConfig as CompressionConfig)?.enabled), // Did the backend ask us to optimize the quality
                    quality: (payload.compressionConfig as CompressionConfig)?.quality, // What did the backend ask us to use as the quality argument
                    originalFileSize: payload.uncompressedFile.size / 1000, // In KBs
                    compressedFileSize: (payload.compressionConfig as CompressionConfig)?.enabled
                        ? payload.file.size / 1000 // In KBs
                        : undefined,
                    duration: Date.now() - fileUploadTimeMap[payload.file.id], // milliseconds it took to complete
                });
                delete fileUploadTimeMap[payload.file.id];
            }
        }),
        ignoreElements(),
    );

const readFile = action$ =>
    action$.pipe(
        ofType(FILE_UPLOAD_ADD_FILE),
        filter(({payload}) => payload.file.type.match(/video/)),
        mergeMap(({payload}) => {
            const file = payload.file as File;

            return captureVideoMetaData(file).pipe(
                mergeMap(([duration, image]) =>
                    of(
                        Actions.readFileSuccess(payload.formName, file, {
                            duration,
                            image: image === 'NO_IMAGE' ? undefined : image.thumbnail.url,
                        }),
                    ),
                ),
            );
        }),
    );

export default combineEpics(readFile, listenForFileUpload, listenForFileUploadCompletion);
